import { Component, computed, effect, ElementRef, HostListener, input, output, signal } from '@angular/core';
import { KopConfigService } from '../../core/kop-config.service';
import { JSBridgeService } from '../../core/jsbridge.service';
import { cKC_Servo } from '@wasm/KopWeb';
import { ModuleServo, ModuleParam, RangedKanaalInput, AnalogOut } from '../PropTypes';
import { wasmVectorToArray } from '../../utils/wasmVector';
import { KopModulesService } from '../../modules/kop-modules.service';
import { KanaalService } from '../../kanaal/kanaal.service';
import { kanaalLabelHdw } from '../../kanaal/kanaal.utils';
import { TranslateService } from '../../i18n/translate.service';
import { BaseElementComponent } from '../../keynav/base-element/base-element.component';
import { FocusManagerService } from '../../keynav/focus-manager.service';

@Component({
  selector: 'servo-editor',
  templateUrl: './servo-editor.component.html',
  styleUrl: './servo-editor.component.scss',
})
export class ServoEditorComponent extends BaseElementComponent{

  constructor(public kopConfigService: KopConfigService, public jsBridgeService: JSBridgeService,
    public kopModulesService: KopModulesService, public kanaalService: KanaalService,
    private translate: TranslateService, focusManager: FocusManagerService, elementRef: ElementRef<HTMLElement>) {

    super(elementRef,focusManager);
    this.keyProperty = "servo-editor";
    effect(() => {
      let initValue = this.initialValue();
      if (initValue != undefined) {
        let meta = this.meta();
        this.editValue = { ...initValue };
        //If params are missing, fill them with default values
        if (meta != undefined) {
          let params = this.kopConfigService.buildDefaultCKParams(meta.param);
          for (let i = 0; i < 4; i++) {
            if (this.editValue.params[i] == undefined && params[i] != undefined) {
              this.editValue.params[i] = params[i];
            }
          }
        }
      }
    });
  }

  public editOnly = input<boolean>(false);
  public onlyDesc = input<boolean>(false);
  public onlyContents = input<boolean>(false);


  public isNew = input<boolean>(false);
  private _metaIdx = signal<number | undefined>(undefined);

  public metaIdx = input<number | undefined>(undefined);
  public isValid = true;

  private newMetaIdx = computed(() => {
    if (this._metaIdx() != undefined) {
      return this._metaIdx();
    }
    return this.metaIdx();
  });
  // public servo = input<cKC_Servo>();
  public valueChanged = output<ModuleServo>();

  public initialValue = input<ModuleServo>();

  public editValue: ModuleServo = {
    index: 0,
    uitgang: 0,
    inverted: false,
    volt: false,
    looptijd: "00:00",
    modbusNetworkId: 0,
    soort: 0,
    stationnr: 0,
    params: {}
  };

  public onParamKanaalChange(event: RangedKanaalInput, pNo: number) {
    this.editValue.params[pNo].inverted = event.inverted == true;
    this.editValue.params[pNo].waarde = event.start;
  }

  public onKanaalInputChanged(event: RangedKanaalInput) {
    this.editValue.inverted = event.inverted == true;
    this.editValue.uitgang = event.start;
    this.editValue.volt = event.volt == true;
  }

  public newServoOptions = computed(() => {
    if (this.isNew()) {
      let servoMetas = this.kopConfigService.currentMeta().servos;
      let tmServos = this.kopConfigService.selectedAfd()?.afdeling().servos;
      if (tmServos != undefined) {
        let selectedModule = this.kopModulesService.selectedAfdModule();
        let ret = servoMetas.filter(sm => {
          if (selectedModule != undefined) {
            if (selectedModule.meta.allServos == undefined) {
              return false;
            }
            if (selectedModule.meta.allServos.indexOf(sm.index) < 0) {
              return false;
            }
          }
          return true;
        })
          .map(pm => { return { index: pm.index, label: pm.omschr.toString() } });
        return ret;
      }
    }
    return [];
  });

  public components = computed(() => {
    let ms = this.initialValue();
    if (ms == undefined) {
      return undefined;
    }
    return this.servoColumns(ms);
  });

  public servoColumns(s: ModuleServo) {
    let servosMetas = this.kopConfigService.currentMeta().servos;
    let meta = servosMetas.find(m => m.index == s.index);
    let typeNames = this.jsBridgeService.servoTypeName;
    let type = typeNames.find(t => t.type == s.soort);
    let typeText = type?.text;
    let kanaalType = this.kanaalService.getServoKanaalType({
      soort: s.soort,
      uitgang: s.uitgang,
      stationnr: s.stationnr,
      modbusNetworkId: s.modbusNetworkId
    });
    if (AnalogOut.equals(kanaalType?.ioType)) {
      typeText = typeText + (s.volt ? "2-10" : "0-10");
    }
    if (s.soort == 1) {
      let formatedTijd = s.looptijd.substring(0, 2) + ":" + s.looptijd.substring(2, 4);
      typeText = typeText + " (" + formatedTijd + ")";
    }
    let uitgang = "";
    if (s.soort == 6) {
      let mbNetworks = this.modbusNetworks();
      let network = mbNetworks.find(n => n.index == s.modbusNetworkId);
      if (network != undefined) {
        uitgang = `${network.label} (${s.stationnr})`;
      }
    } else {
      if (kanaalType != undefined) {
        uitgang = kanaalLabelHdw(s.uitgang, kanaalType.ioType) + (s.inverted ? '!' : '');

      }
    }
    let params = [];
    for (let i = 0; i < 4; i++) {
      let param = s.params[i];
      if (param != undefined) {
        let type = this.kanaalService.getIOTypeFromParamType(param?.type);
        if (type != undefined) {
          let puit = kanaalLabelHdw(param.waarde, type);
          puit = puit + (param.inverted ? '!' : '');
          params.push(puit);
        } else {
          params.push(`pa: ${param.waarde}`);
        }
      } else {
        params.push(``);
      }
    }
    return {
      index: s.index,
      omschrijving: meta?.omschr.toString(),
      soort: typeText,
      uitgang: uitgang,
      params: params

    }
  }

  alreadySelectedFN() {
    let tmServos = this.kopConfigService.selectedAfd()?.afdeling().servos;
    if (tmServos != undefined) {
      let servos = wasmVectorToArray(tmServos);
      let ret = (idx: number) => {
        return servos.find(a => a.index == idx) != undefined;
      }
      return ret;
    }
    return (idx: number) => false;
  }


  labelFn() {
    let ret = (meta: any) => {
      //(meta as Losdef).omschr.toString()
      let m = meta as any;
      return this.translate.translateKopData('servo', m.index, m.label);
    };
    return ret;
  }


  public modbusNetworks = computed(() => {
    let kopConfig = this.jsBridgeService.kopConfig();
    if (kopConfig != undefined) {
      let mb = wasmVectorToArray(kopConfig.gModBusNetworks);
      let ret = mb.map(m => {
        return {
          index: m.idNr,
          label: m.name.toString()
        }
      });
      ret.unshift({ index: 0, label: 'Geen' });
      return ret;
    }
    return [];
  });

  public findMBNetwork(idx: number | undefined) {
    if (idx == undefined) {
      return "";
    }
    let mbs = this.modbusNetworks();
    let m = mbs.find(m => m.index == idx);
    if (m != undefined) {
      return m.label;
    }
    return "";
  }

  onNew(e: { index: number }) {
    let idx = e.index;
    this._metaIdx.set(idx);
    this.editValue = this.kopConfigService.buildDefaultServo(idx);
    this.open();
  }


  public meta = computed(() => {
    let servo = this.initialValue();
    if (servo != undefined) {
      let meta = this.kopConfigService.currentMeta();
      return meta.servos.find(m => m.index == servo.index);
    }
    let chosenMeta = this.newMetaIdx();
    if (chosenMeta != undefined) {
      let meta = this.kopConfigService.currentMeta();
      return meta.servos.find(m => m.index == chosenMeta);
    }
  });


  public assignment = computed(() => {
    let meta = this.meta();
    let afd = this.kopConfigService.selectedAfd();
    if (meta != undefined && afd != undefined) {
      return this.kanaalService.buildAssignementInfo('servo', afd?.afdeling(), meta);
    }
  });


  public paramsMeta = computed(() => {
    let meta = this.meta();
    let afd = this.kopConfigService.selectedAfd();
    if (meta != undefined && afd != undefined) {
      let pmetas = wasmVectorToArray(meta.param);
      let ret = pmetas.map(pmeta => {
        return {
          pmeta: pmeta,
          assign: this.kanaalService.buildAssignementInfo('servoparam', afd?.afdeling(), meta, pmeta.no)
        }
      });
      return ret;
    }
    return [];
  });

  servoParamTypeLabel(sp: ModuleParam) {
    let kanaalType = this.kanaalService.getKCParamType(sp);
    if (kanaalType == undefined) {
      return "Parameter";
    }
    return `${kanaalType?.ioType.type} ${kanaalType?.ioType.direction}`;
  }


  servoTypes() {
    return this.jsBridgeService.servoTypeName;
  }

  onEdit() {
    this.open();
    this.isValid = true;
  }

  checkValid() {
    setTimeout(() => {
      this.isValid = true;
      if (this.editValue.soort == 6 && this.editValue.stationnr != 0 && this.editValue.modbusNetworkId == 0) {
        this.isValid = false;
      }
    }, 100);
  }

  onDelete() {
    let servo = this.initialValue();
    this.kopConfigService.applyAfdUpdate(afd => {
      if ((servo != undefined) && (afd != undefined)) {
        this.kopConfigService.eraseServo(afd, servo);
      }
      return afd;
    });
    this.close();

  }

  onSave() {
    this.close();
    console.log(this.editValue);
    // if (this.editValue.uitgang == 0 || (this.editValue.soort == 6 && this.editValue.stationnr == 0)) {
    //   this.onDelete();
    //   return;
    // }
    if (this.editOnly()) {
      this.valueChanged.emit(this.editValue);
      return;
    }
    let meta = this.meta();
    if (meta != undefined) {
      this.kopConfigService.upsertServo(this.editValue, meta);
    }

  }
}
